/**
 * src/features/admin/ui/MrrGrid.tsx
 *
 * MRR cards row: PLN and EUR (separate — never combined, D-02) (ADMIN-01).
 * Extracted from MetricsGrid to keep component size under 200 lines (CLAUDE.md rule 5).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/ui/card';
import { roundTo } from '@/shared/lib/number';
import type { AdminMetrics } from '@/shared/types/admin';

function formatMrr(value: number, currency: string): string {
  return (
    roundTo(value, 2).toLocaleString('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) +
    ' ' +
    currency
  );
}

export function MrrGrid({ mrr }: { mrr: AdminMetrics['mrr'] }): React.ReactElement {
  const { t } = useTranslation();

  const mrrCards: Array<{ currency: 'PLN' | 'EUR'; value: number }> = [
    { currency: 'PLN', value: mrr.PLN },
    { currency: 'EUR', value: mrr.EUR },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
      {mrrCards.map(({ currency, value }) => (
        <Card key={currency}>
          <CardContent className="p-6 flex flex-col gap-2">
            <dt className="text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">
              {t('admin.metric.mrr', 'MRR')} · {currency}
            </dt>
            <dd className="text-subhead font-bold font-mono tabular-nums text-green-600 dark:text-green-400">
              {formatMrr(value, currency)}
            </dd>
            <p className="text-caption text-slate-400 dark:text-slate-500">
              {t('admin.metric.mrr_note', 'Active subs only · monthly')}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
